import { useEffect, useMemo, useState } from "react";
import { Button } from "../Button";
import { PopupModal } from "../PopupModal";
import { api } from "../../services/api";
import { ShowToast } from "../../services/toast";
import { Loading } from "../Loading/Loading";
import { ReactComponent as CloseIcon } from "../../assets/icons/x.svg?react";
import { ReactComponent as TrashIcon } from "../../assets/icons/trash.svg?react";
import { BillingCacheStats, CacheActionDefinition } from "../../types/cache.types";

interface CacheClearingModalProps {
  onClose: () => void;
}

export function CacheClearingModal({ onClose }: CacheClearingModalProps) {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [stats, setStats] = useState<BillingCacheStats | null>(null);
  const [activeActionId, setActiveActionId] = useState<string | null>(null);

  const numberFormatter = useMemo(() => new Intl.NumberFormat(), []);

  const formatBytes = (bytes?: number) => {
    if (bytes === undefined || bytes === null || Number.isNaN(bytes)) {
      return "—";
    }
    if (bytes === 0) {
      return "0 B";
    }
    const units = ["B", "KB", "MB", "GB", "TB"];
    const index = Math.floor(Math.log(bytes) / Math.log(1024));
    const value = bytes / Math.pow(1024, index);
    return `${value.toFixed(value >= 100 ? 0 : value >= 10 ? 1 : 2)} ${units[index]}`;
  };

  const formatDateTime = (value?: string) => {
    if (!value) {
      return "Not scheduled";
    }
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) {
      return "Not available";
    }
    const diffMs = parsed.getTime() - Date.now();
    const diffHours = Math.round(diffMs / (1000 * 60 * 60));
    const relativeFormatter = new Intl.RelativeTimeFormat(undefined, {
      numeric: "auto",
    });
    return `${parsed.toLocaleString()} (${relativeFormatter.format(diffHours, "hour")})`;
  };

  const fetchCacheStats = async (withLoader = true) => {
    if (withLoader) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }
    try {
      const info = await api.getBillingCacheStats();
      setStats(info);
      return info;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to load billing cache stats.";
      ShowToast({
        type: "error",
        message: errorMessage,
      });
      throw error;
    } finally {
      if (withLoader) {
        setLoading(false);
      } else {
        setRefreshing(false);
      }
    }
  };

  const handleAction = async (action: CacheActionDefinition) => {
    const url = action.path;
    try {
      setActiveActionId(action.id);
      const response = await api.performCacheAction({
        method: action.method,
        path: url,
      });
      const clearedDetails =
        response?.clearedKeys !== undefined ? ` (${response.clearedKeys} keys)` : "";
      ShowToast({
        type: "success",
        message: `${response?.message || `${action.label} triggered successfully.`}${clearedDetails}`,
      });
      await fetchCacheStats(false);
    } catch (error: unknown) {
      const errorMessage =
        (error as any)?.response?.data?.message ||
        (error instanceof Error ? error.message : `Failed to run ${action.label.toLowerCase()}.`);
      ShowToast({
        type: "error",
        message: errorMessage,
      });
    } finally {
      setActiveActionId(null);
    }
  };

  const actions = (stats?.actions ?? []).filter(
    (action) => !action.requiresUserId && !action.requiresPattern
  );

  const getActionTone = (action: CacheActionDefinition) => {
    const id = action.id?.toLowerCase?.() ?? "";
    if (id.includes("clear-all")) {
      return "danger";
    }
    if (id.includes("clear-billing")) {
      return "warning";
    }
    return "default";
  };

  const getDefaultDescription = (action: CacheActionDefinition) => {
    const id = action.id?.toLowerCase?.() ?? "";
    switch (id) {
      case "clear-all":
        return "Clears every cache entry across Redis and the in-memory fallback.";
      case "clear-billing":
        return "Clears shared billing cache entries without touching authentication or user caches.";
      default:
        return `Executes the ${action.label} operation.`;
    }
  };

  const getActionButtonLabel = (action: CacheActionDefinition) => {
    const id = action.id?.toLowerCase?.() ?? "";
    if (id.includes("clear-all")) {
      return "Clear everything";
    }
    if (id.includes("clear-billing")) {
      return "Clear billing cache";
    }
    return "Run action";
  };

  const disableActions = refreshing || activeActionId !== null;

  useEffect(() => {
    fetchCacheStats();
  }, []);

  return (
    <PopupModal
      onClose={onClose}
      contentClassName="p-8 z-[2] w-[95%] md:w-[98%] self-center bg-white rounded-2xl flex-col justify-start items-center gap-6 flex w-full md:mx-auto md:w-[640px] max-w-[640px]"
    >
      <div className="flex flex-col w-full">
        <div className="flex justify-between items-start mb-4">
          <div>
            <h1 className="text-black text-xl font-semibold">Billing Cache Controls</h1>
            <p className="text-sm text-black/60 mt-1">
              These actions purge the shared billing cache immediately. Use them sparingly outside of
              scheduled maintenance windows.
            </p>
          </div>
          <button onClick={onClose} className="group" aria-label="Close cache modal">
            <CloseIcon className="w-6 h-6" />
          </button>
        </div>

        {loading ? (
          <div className="w-full flex justify-center my-5">
            <Loading />
          </div>
        ) : (
          <>
            {stats && (
              <div className="mb-6 p-5 bg-[#F9FAFB] border border-[#E4E7EC] rounded-xl">
                <div className="flex items-start justify-between gap-3 mb-4">
                  <div>
                    <h3 className="text-lg font-medium">Current Status</h3>
                    <p className="text-sm text-black/50 mt-1">
                      Metrics refresh every time a cache reset runs or when you manually refresh.
                    </p>
                  </div>
                  <Button
                    variant="outline"
                    className="!rounded-full !text-xs !py-1.5 !px-4"
                    onClick={() => fetchCacheStats(false)}
                    disabled={refreshing || disableActions}
                    loading={refreshing}
                  >
                    Refresh
                  </Button>
                </div>

                <dl className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-black/50">
                      Cache mode
                    </dt>
                    <dd className="text-base font-semibold text-black mt-1">
                      {stats.cacheType?.toUpperCase?.() || stats.cacheType || "Unknown"}
                    </dd>
                    <dd className="text-xs text-black/50 mt-0.5">
                      Auto-clears daily at 8:00 AM server time
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-black/50">
                      Redis connection
                    </dt>
                    <dd
                      className={`text-base font-semibold mt-1 ${
                        stats.redisConnected ? "text-[#047857]" : "text-[#B91C1C]"
                      }`}
                    >
                      {stats.redisConnected ? "Connected" : "Using in-memory fallback"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-black/50">
                      Memory entries
                    </dt>
                    <dd className="text-base font-semibold text-black mt-1">
                      {numberFormatter.format(stats.memoryEntries)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-black/50">
                      Memory footprint
                    </dt>
                    <dd className="text-base font-semibold text-black mt-1">
                      {formatBytes(stats.memoryApproxBytes)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-black/50">
                      Redis memory usage
                    </dt>
                    <dd className="text-base font-semibold text-black mt-1">
                      {formatBytes(stats.redisUsedMemoryBytes)}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs font-medium uppercase tracking-wide text-black/50">
                      Next automatic clear
                    </dt>
                    <dd className="text-base font-semibold text-black mt-1">
                      {formatDateTime(stats.nextClearTime)}
                    </dd>
                  </div>
                </dl>
              </div>
            )}

            <div className="space-y-5">
              {actions.length > 0 && (
                <div className="border border-[#E4E7EC] rounded-xl overflow-hidden">
                  <div className="grid grid-cols-[1.9fr,1.7fr,auto] gap-4 bg-[#F9FAFB] border-b border-[#E4E7EC] text-[11px] font-semibold uppercase tracking-wide text-black/45 px-4 py-2">
                    <span>Action</span>
                    <span>Endpoint</span>
                    <span className="text-right">Trigger</span>
                  </div>
                  <div className="divide-y divide-[#E4E7EC]">
                    {actions.map((action) => {
                      const tone = getActionTone(action);
                      const rowClass =
                        tone === "danger"
                          ? "bg-[#FEF3F2]"
                          : tone === "warning"
                          ? "bg-[#FFF7ED]"
                          : "bg-white";
                      const buttonClass =
                        tone === "danger"
                          ? "!bg-red-600 hover:!bg-red-700 text-white"
                          : tone === "warning"
                          ? "!bg-amber-500 hover:!bg-amber-600 text-white"
                          : "";
                      const description = action.description || getDefaultDescription(action);
                      const lastRun =
                        tone === "danger"
                          ? stats?.timestamp
                            ? new Date(stats.timestamp).toLocaleString()
                            : "unknown"
                          : null;

                      return (
                        <div
                          key={action.id}
                          className={`grid grid-cols-[1.9fr,1.7fr,auto] gap-4 items-center px-4 py-3 text-sm leading-snug ${rowClass}`}
                        >
                          <div className="flex items-start gap-2">
                            {tone === "danger" && <TrashIcon className="w-3.5 h-3.5 text-[#F04438] mt-0.5" />}
                            <div>
                              <div className="font-medium text-black text-sm">{action.label}</div>
                              <div className="text-[11px] text-black/55 mt-0.5">{description}</div>
                              {lastRun && (
                                <div className="text-[11px] text-[#7A271A] mt-0.5">
                                  Last manual action: {lastRun}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-[11px] text-black/60 whitespace-pre-line">
                            {action.method.toUpperCase()} {action.path}
                          </div>
                          <div className="flex justify-end">
                            <Button
                              variant="outline"
                              className={`!rounded-full !px-4 !py-2 !text-xs !font-semibold ${buttonClass}`}
                              onClick={() => handleAction(action)}
                              loading={activeActionId === action.id}
                              disabled={disableActions}
                            >
                              {getActionButtonLabel(action)}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </PopupModal>
  );
}
